import React, { useState, useEffect, useCallback } from 'react';
import DesktopOnlyNotice from './DesktopOnlyNotice';
import { apiService } from './apiService';
import './SuperAdminView.css';
import WorkflowCanvas from './WorkflowCanvas';
import ExecutionLog from './ExecutionLog';
import SAAIPlatformSettings from './SAAIPlatformSettings';
import SAWhatsAppSessions from './SAWhatsAppSessions';

// ═══════════════════════════════════════════════════════════════════
// SUPER ADMIN VIEW — GoWarmCRM Platform Administration
// Only accessible to users in the super_admins table
// ═══════════════════════════════════════════════════════════════════

const SA_TABS = [
  { id: 'overview',          label: 'Overview',          icon: '📊' },
  { id: 'orgs',              label: 'Organisations',     icon: '🏢' },
  { id: 'users',             label: 'Users',             icon: '👥' },
  { id: 'admins',            label: 'Super Admins',      icon: '🔐' },
  { id: 'audit',             label: 'Audit Log',         icon: '📋' },
  { id: 'workflows',         label: 'Workflows',         icon: '⚙️'  },
  { id: 'platform-settings', label: 'Platform Settings', icon: '🛠️'  },
];

export default function SuperAdminView(props) {
  return (
    <DesktopOnlyNotice
      title="Super admin needs a wider screen"
      detail="Platform administration works across wide tables of organisations, users and usage. These are dense by design and do not compress well."
    >
      <SuperAdminViewInner {...props} />
    </DesktopOnlyNotice>
  );
}

function SuperAdminViewInner() {
  const [tab, setTab]         = useState('overview');
  const [tabOpts, setTabOpts] = useState({}); // initial filters when navigating from a tile

  // Tiles (and anything else) navigate with optional initial filters,
  // e.g. navigate('orgs', { status: 'trial' }) or navigate('users', { new30d: true })
  const navigate = (tabId, opts = {}) => {
    setTabOpts(opts);
    setTab(tabId);
  };

  return (
    <div className="sa-view">
      <div className="sa-header">
        <div className="sa-header-left">
          <div className="sa-badge">⚡ Super Admin</div>
          <h1>GoWarmCRM Platform</h1>
          <p className="sa-subtitle">Platform-level administration — changes affect all organisations</p>
        </div>
      </div>

      <div className="sa-tabs">
        {SA_TABS.map(t => (
          <button
            key={t.id}
            className={`sa-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => navigate(t.id)}
          >
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      <div className="sa-body">
        {tab === 'overview'          && <SAOverview onNavigate={navigate} />}
        {tab === 'orgs'              && <SAOrgs initialStatus={tabOpts.status || ''} />}
        {tab === 'users'             && <SAUsers initialNew30d={!!tabOpts.new30d} />}
        {tab === 'admins'            && <SAAdmins />}
        {tab === 'audit'             && <SAAuditLog />}
        {tab === 'workflows'         && <SAWorkflows />}
        {tab === 'platform-settings' && <SAPlatformSettings />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// OVERVIEW — key platform metrics
// ─────────────────────────────────────────────────────────────────

function SAOverview({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiService.superAdmin.getStats()
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="sa-loading">Loading platform stats...</div>;
  if (!stats)  return null;

  const cards = [
    { label: 'Total Orgs',        value: stats.orgs.total_orgs,       sub: `${stats.orgs.new_orgs_30d} new this month`,   icon: '🏢', color: 'blue',   go: ['orgs']                    },
    { label: 'Active Orgs',       value: stats.orgs.active_orgs,      sub: `${stats.orgs.suspended_orgs} suspended`,      icon: '✅', color: 'green',  go: ['orgs',  { status: 'active' }] },
    { label: 'Trial Orgs',        value: stats.orgs.trial_orgs,       sub: 'need conversion',                             icon: '⏰', color: 'amber',  go: ['orgs',  { status: 'trial' }]  },
    { label: 'Total Users',       value: stats.users.total_users,     sub: `${stats.users.active_users} active`,          icon: '👥', color: 'purple', go: ['users']                   },
    { label: 'New Users (30d)',    value: stats.users.new_users_30d,   sub: 'recent signups',                              icon: '📈', color: 'teal',   go: ['users', { new30d: true }]     },
    { label: 'Actions (7d)',       value: stats.activity.actions_7d,   sub: `${stats.activity.actions_24h} today`,         icon: '⚡', color: 'indigo', go: ['audit']                   },
  ];

  return (
    <div className="sa-overview">
      <div className="sa-stat-grid">
        {cards.map(c => (
          <button
            key={c.label}
            type="button"
            className={`sa-stat-card sa-stat-card--${c.color} sa-stat-card--clickable`}
            onClick={() => onNavigate && onNavigate(c.go[0], c.go[1] || {})}
            title={`Open ${c.label}`}
          >
            <div className="sa-stat-icon">{c.icon}</div>
            <div className="sa-stat-value">{Number(c.value).toLocaleString()}</div>
            <div className="sa-stat-label">{c.label}</div>
            <div className="sa-stat-sub">{c.sub}</div>
          </button>
        ))}
      </div>

      <div className="sa-info-banner">
        <span className="sa-info-icon">ℹ️</span>
        <span>Super admin actions are fully audited. Every org modification, user change, and impersonation session is logged in the Audit Log tab.</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ORGS — list, create, edit, suspend, impersonate
// ─────────────────────────────────────────────────────────────────

function SAOrgs({ initialStatus = '' }) {
  const [orgs, setOrgs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatus] = useState(initialStatus);
  const [planFilter, setPlan]     = useState('');
  const [page, setPage]           = useState(1);
  const [selectedOrg, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // org being deleted
  const [deleteInput, setDeleteInput]   = useState('');   // typed confirmation

  const LIMIT = 20;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiService.superAdmin.getOrgs({ search, status: statusFilter, plan: planFilter, page, limit: LIMIT });
      setOrgs(r.data.orgs);
      setTotal(r.data.total);
    } catch (e) {
      setError('Failed to load organisations');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, planFilter, page]);

  useEffect(() => { load(); }, [load]);

  const handleSuspend = async (org, suspend) => {
    const reason = suspend ? window.prompt(`Reason for suspending "${org.name}"?`) : null;
    if (suspend && reason === null) return; // cancelled
    try {
      await apiService.superAdmin.suspendOrg(org.id, { suspend, reason });
      setSuccess(suspend ? `${org.name} suspended` : `${org.name} reactivated`);
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Action failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleteInput !== deleteTarget.name) return;
    try {
      const API   = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const r = await fetch(`${API}/super/orgs/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.error?.message || 'Delete failed');
      setDeleteTarget(null);
      setDeleteInput('');
      setSuccess(data.message);
      setTimeout(() => setSuccess(''), 4000);
      load();
    } catch (e) {
      setError(e.message || 'Failed to delete organisation');
      setDeleteTarget(null);
      setDeleteInput('');
    }
  };

  const handleImpersonate = async (org) => {
    if (!window.confirm(`You are about to enter support mode for "${org.name}". This will be logged. Continue?`)) return;
    try {
      const r = await apiService.superAdmin.impersonateOrg(org.id);
      setSuccess(`Support mode active: ${r.data.supportOrgName}`);
      // Store support context — org admin UI can read this
      sessionStorage.setItem('supportOrgId',   r.data.supportOrgId);
      sessionStorage.setItem('supportOrgName', r.data.supportOrgName);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError('Impersonation failed');
    }
  };

  const STATUS_COLORS = { active: 'green', suspended: 'red', trial: 'amber', cancelled: 'grey' };
  const PLAN_LABELS   = { free: 'Free', starter: 'Starter', pro: 'Pro', enterprise: 'Enterprise' };

  return (
    <div className="sa-panel">
      {error   && <div className="sa-alert sa-alert--error">⚠️ {error}<button onClick={() => setError('')}>✕</button></div>}
      {success && <div className="sa-alert sa-alert--success">✅ {success}</div>}

      {/* Toolbar */}
      <div className="sa-toolbar">
        <input
          className="sa-search"
          placeholder="Search organisations…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select className="sa-select" value={statusFilter} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select className="sa-select" value={planFilter} onChange={e => { setPlan(e.target.value); setPage(1); }}>
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <button className="sa-btn-primary" onClick={() => setShowCreate(true)}>
          + New Organisation
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="sa-loading">Loading…</div>
      ) : (
        <>
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Members</th>
                  <th>Owner</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.length === 0 && (
                  <tr><td colSpan={7} className="sa-empty">No organisations found</td></tr>
                )}
                {orgs.map(org => (
                  <tr key={org.id} className={org.status === 'suspended' ? 'sa-row-suspended' : ''}>
                    <td>
                      <button className="sa-link" onClick={() => setSelected(org)}>
                        {org.name}
                      </button>
                      {org.type === 'assessment' && (
                        <span className="sa-badge-status sa-badge-status--amber" style={{ marginLeft: 6 }}>assessment</span>
                      )}
                      <div className="sa-sub-text">ID: {org.id}</div>
                    </td>
                    <td>
                      <span className={`sa-badge-status sa-badge-status--${STATUS_COLORS[org.status]}`}>
                        {org.status}
                      </span>
                    </td>
                    <td><span className="sa-plan-pill">{PLAN_LABELS[org.plan] || org.plan}</span></td>
                    <td>{org.member_count} / {org.max_users}</td>
                    <td className="sa-sub-text">{org.owner_email || '—'}</td>
                    <td className="sa-sub-text">{new Date(org.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="sa-action-btns">
                        <button className="sa-btn-sm" onClick={() => setSelected(org)} title="View details">
                          👁
                        </button>
                        <button className="sa-btn-sm sa-btn-sm--blue" onClick={() => handleImpersonate(org)} title="Support mode">
                          🔧
                        </button>
                        {org.status === 'suspended' ? (
                          <button className="sa-btn-sm sa-btn-sm--green" onClick={() => handleSuspend(org, false)} title="Reactivate">
                            ▶
                          </button>
                        ) : (
                          <button className="sa-btn-sm sa-btn-sm--red" onClick={() => handleSuspend(org, true)} title="Suspend">
                            ⏸
                          </button>
                        )}
                        <button
                          className="sa-btn-sm sa-btn-sm--red"
                          onClick={() => { setDeleteTarget(org); setDeleteInput(''); }}
                          title="Permanently delete org"
                          style={{ opacity: 0.7 }}
                        >
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="sa-pagination">
            <span className="sa-sub-text">{total} total</span>
            <div className="sa-page-btns">
              <button className="sa-btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <span className="sa-page-num">Page {page}</span>
              <button className="sa-btn-sm" disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        </>
      )}

      {/* Org Detail Drawer */}
      {selectedOrg && (
        <SAOrgDetail
          orgId={selectedOrg.id}
          onClose={() => { setSelected(null); load(); }}
        />
      )}

      {/* Create Org Modal */}
      {showCreate && (
        <SACreateOrg
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); setSuccess('Organisation created'); setTimeout(() => setSuccess(''), 3000); }}
        />
      )}

      {/* Delete Org Confirmation Modal */}
      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '28px 32px',
            width: 420, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>🗑️ Delete Organisation</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
              {deleteTarget.name}
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: '0 0 20px' }}>
              This will <strong>permanently delete all data</strong> for this organisation —
              members, deals, playbooks, stages, contacts, emails, and everything else.
              This cannot be undone.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                Type <strong>{deleteTarget.name}</strong> to confirm:
              </label>
              <input
                autoFocus
                value={deleteInput}
                onChange={e => setDeleteInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && deleteInput === deleteTarget.name && handleDelete()}
                placeholder={deleteTarget.name}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 7, boxSizing: 'border-box',
                  border: `1.5px solid ${deleteInput === deleteTarget.name ? '#ef4444' : '#d1d5db'}`,
                  fontSize: 14, outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDeleteTarget(null); setDeleteInput(''); }}
                style={{
                  padding: '8px 20px', borderRadius: 7, border: '1px solid #d1d5db',
                  background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151',
                }}
              >
                Cancel
              </button>
              <button
                disabled={deleteInput !== deleteTarget.name}
                onClick={handleDelete}
                style={{
                  padding: '8px 20px', borderRadius: 7, border: 'none',
                  background: deleteInput === deleteTarget.name ? '#ef4444' : '#fca5a5',
                  color: '#fff', fontSize: 13, fontWeight: 600,
                  cursor: deleteInput === deleteTarget.name ? 'pointer' : 'not-allowed',
                  transition: 'background 0.15s',
                }}
              >
                Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ORG DETAIL DRAWER
// ─────────────────────────────────────────────────────────────────

function SAOrgDetail({ orgId, onClose }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm]     = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole]   = useState('member');
  const [converting, setConverting] = useState(false);

  // Create user state
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', first_name: '', last_name: '', password: '', role: 'member' });
  const [creatingUser, setCreatingUser] = useState(false);

  // Invite state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [invites, setInvites] = useState([]);
  const [lastInviteUrl, setLastInviteUrl] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiService.superAdmin.getOrg(orgId);
      setData(r.data);
      setForm({ name: r.data.org.name, plan: r.data.org.plan, max_users: r.data.org.max_users, notes: r.data.org.notes || '' });
      // Load invites
      try {
        const invR = await apiService.superAdmin.getInvites(orgId);
        setInvites(invR.data.invites || []);
      } catch { /* invites table might not exist yet */ }
    } catch { setError('Failed to load org'); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);


  // Assessment → Standard conversion (one-way). Seeds the org on the spot.
  const handleConvertToStandard = async () => {
    if (!window.confirm(
      `Convert "${data?.org?.name}" to a Standard organisation?\n\n` +
      'This is one-way. The org will be seeded with default stages & playbooks, ' +
      'and CRM write-back becomes ENABLE-ABLE (it stays off until explicitly turned on per connection). ' +
      'Frozen assessment baselines are kept as the before-picture.'
    )) return;
    try {
      setConverting(true);
      await apiService.superAdmin.convertOrgToStandard(orgId);
      setSuccess('Converted to Standard and seeded');
      setTimeout(() => setSuccess(''), 4000);
      await load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiService.superAdmin.updateOrg(orgId, form);
      setSuccess('Saved'); setEditing(false);
      setTimeout(() => setSuccess(''), 2000);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleAddUser = async () => {
    if (!addEmail.trim()) return;
    try {
      await apiService.superAdmin.addUserToOrg(orgId, { email: addEmail.trim(), role: addRole });
      setAddEmail(''); setError(''); setSuccess('User added');
      setTimeout(() => setSuccess(''), 2000);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to add user');
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.email.trim() || !createForm.first_name.trim() || !createForm.last_name.trim()) {
      setError('Email, first name, and last name are required'); return;
    }
    if (createForm.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    try {
      setCreatingUser(true);
      setError('');
      await apiService.superAdmin.createUserForOrg(orgId, createForm);
      setCreateForm({ email: '', first_name: '', last_name: '', password: '', role: 'member' });
      setShowCreateUser(false);
      setSuccess('User created and added to org');
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (e) {
      const msg = e.response?.data?.error?.message || 'Failed to create user';
      // If user already exists, offer to add them directly
      if (e.response?.status === 409) {
        const email = createForm.email.trim();
        setError(`${msg} — Would you like to add them?`);
        setAddEmail(email);
        setAddRole(createForm.role);
        setShowCreateUser(false);
      } else {
        setError(msg);
      }
    } finally { setCreatingUser(false); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      setSendingInvite(true);
      const r = await apiService.superAdmin.inviteUserToOrg(orgId, { email: inviteEmail.trim(), role: inviteRole });
      setLastInviteUrl(r.data.inviteUrl || '');
      setInviteEmail('');
      setSuccess('Invite created — copy the link below to share');
      setTimeout(() => setSuccess(''), 5000);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to create invite');
    } finally { setSendingInvite(false); }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await apiService.superAdmin.cancelInvite(orgId, inviteId);
      load();
    } catch (e) {
      setError('Failed to cancel invite');
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await apiService.superAdmin.updateUserInOrg(orgId, userId, { role });
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to update role');
    }
  };

  const handleImpersonateUser = async (m) => {
    const who = m.name || m.email;
    if (!window.confirm(
      `Enter READ-ONLY support mode as "${who}"?\n\n` +
      `You'll see the app exactly as they do. Writes are disabled and this is logged.`
    )) return;
    try {
      setError('');
      const r = await apiService.superAdmin.impersonateUser(m.user_id);
      // Stash the real super-admin session, then swap in the impersonated one.
      localStorage.setItem('sa_token', localStorage.getItem('token'));
      localStorage.setItem('sa_user',  localStorage.getItem('user'));
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user',  JSON.stringify(r.data.user));
      // Full reload so the whole app re-initialises as the impersonated user.
      window.location.reload();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to start impersonation');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Remove this user from the org?')) return;
    try {
      setError('');
      await apiService.superAdmin.removeUserFromOrg(orgId, userId);
      setSuccess('User removed');
      setTimeout(() => setSuccess(''), 2000);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to remove user');
    }
  };

  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let pw = '';
    for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setCreateForm(f => ({ ...f, password: pw }));
  };

  const pendingInvites = invites.filter(i => !i.accepted_at && new Date(i.expires_at) > new Date());
  const expiredInvites = invites.filter(i => !i.accepted_at && new Date(i.expires_at) <= new Date());

  return (
    <div className="sa-drawer-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sa-drawer">
        <div className="sa-drawer-header">
          <h2>{data?.org.name || 'Organisation'}</h2>
          <button className="sa-drawer-close" onClick={onClose}>✕</button>
        </div>

        {loading && <div className="sa-loading">Loading…</div>}

        {!loading && data && (
          <div className="sa-drawer-body">
            {error   && <div className="sa-alert sa-alert--error">⚠️ {error}<button onClick={() => setError('')}>✕</button></div>}
            {success && <div className="sa-alert sa-alert--success">✅ {success}</div>}

            {/* Org info */}
            <section className="sa-drawer-section">
              <div className="sa-section-header">
                <h3>Organisation Details</h3>
                <button className="sa-btn-sm" onClick={() => setEditing(!editing)}>
                  {editing ? 'Cancel' : '✏️ Edit'}
                </button>
              </div>

              {editing ? (
                <div className="sa-form-grid">
                  <div className="sa-form-field">
                    <label>Name</label>
                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="sa-form-field">
                    <label>Plan</label>
                    <select value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })}>
                      {['free','starter','pro','enterprise'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="sa-form-field">
                    <label>Max Users</label>
                    <input type="number" value={form.max_users} onChange={e => setForm({ ...form, max_users: parseInt(e.target.value) })} />
                  </div>
                  <div className="sa-form-field sa-form-field--full">
                    <label>Notes</label>
                    <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} />
                  </div>
                  <button className="sa-btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : '💾 Save Changes'}
                  </button>
                </div>
              ) : (
                <div className="sa-info-grid">
                  <div className="sa-info-row"><span>ID</span><strong>{data.org.id}</strong></div>
                  <div className="sa-info-row">
                    <span>Type</span>
                    <strong>
                      {data.org.type === 'assessment' ? (
                        <span className="sa-badge-status sa-badge-status--amber">assessment (read-only, unseeded)</span>
                      ) : (
                        <>standard{data.org.converted_to_standard_at
                          ? ` — converted ${new Date(data.org.converted_to_standard_at).toLocaleDateString()}`
                          : ''}</>
                      )}
                    </strong>
                  </div>
                  {data.org.type === 'assessment' && (
                    <div className="sa-info-row">
                      <span>Convert</span>
                      <strong>
                        <button
                          className="sa-btn-sm sa-btn-sm--green"
                          onClick={handleConvertToStandard}
                          disabled={converting}
                        >
                          {converting ? 'Converting…' : '⤴ Convert to Standard'}
                        </button>
                      </strong>
                    </div>
                  )}
                  <div className="sa-info-row"><span>Status</span><strong>{data.org.status}</strong></div>
                  <div className="sa-info-row"><span>Plan</span><strong>{data.org.plan}</strong></div>
                  <div className="sa-info-row"><span>Seats</span><strong>{data.members.filter(m => m.is_active).length} / {data.org.max_users}</strong></div>
                  <div className="sa-info-row"><span>Created</span><strong>{new Date(data.org.created_at).toLocaleDateString()}</strong></div>
                  {data.org.notes && <div className="sa-info-row"><span>Notes</span><strong>{data.org.notes}</strong></div>}
                </div>
              )}
            </section>

            {/* Members */}
            <section className="sa-drawer-section">
              <div className="sa-section-header">
                <h3>Members ({data.members.length})</h3>
                <div className="sa-section-header-actions">
                  <button className="sa-btn-sm sa-btn-sm--blue" onClick={() => { setShowCreateUser(!showCreateUser); setShowInvite(false); }}>
                    {showCreateUser ? '✕ Close' : '➕ Create User'}
                  </button>
                  <button className="sa-btn-sm sa-btn-sm--green" onClick={() => { setShowInvite(!showInvite); setShowCreateUser(false); }}>
                    {showInvite ? '✕ Close' : '📧 Invite'}
                  </button>
                </div>
              </div>

              {/* Create User Form */}
              {showCreateUser && (
                <div className="sa-create-user-form">
                  <div className="sa-form-section-title">Create New User Account</div>
                  <div className="sa-form-grid">
                    <div className="sa-form-field">
                      <label>First Name *</label>
                      <input
                        value={createForm.first_name}
                        onChange={e => setCreateForm({ ...createForm, first_name: e.target.value })}
                        placeholder="John"
                      />
                    </div>
                    <div className="sa-form-field">
                      <label>Last Name *</label>
                      <input
                        value={createForm.last_name}
                        onChange={e => setCreateForm({ ...createForm, last_name: e.target.value })}
                        placeholder="Doe"
                      />
                    </div>
                    <div className="sa-form-field sa-form-field--full">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={createForm.email}
                        onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                        placeholder="user@company.com"
                      />
                    </div>
                    <div className="sa-form-field">
                      <label>Temp Password *</label>
                      <div className="sa-password-row">
                        <input
                          value={createForm.password}
                          onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                          placeholder="Min 8 characters"
                        />
                        <button className="sa-btn-sm" onClick={generateTempPassword} title="Generate password">🎲</button>
                      </div>
                    </div>
                    <div className="sa-form-field">
                      <label>Role</label>
                      <select value={createForm.role} onChange={e => setCreateForm({ ...createForm, role: e.target.value })}>
                        <option value="owner">Owner</option>
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </div>
                  </div>
                  <div className="sa-form-actions">
                    <button className="sa-btn-primary" onClick={handleCreateUser} disabled={creatingUser}>
                      {creatingUser ? 'Creating…' : '✅ Create & Add to Org'}
                    </button>
                    <span className="sa-form-hint">User will be prompted to change password on first login</span>
                  </div>
                </div>
              )}

              {/* Invite Form */}
              {showInvite && (
                <div className="sa-invite-form">
                  <div className="sa-form-section-title">Send Invite Link</div>
                  <div className="sa-add-user-row">
                    <input
                      className="sa-input-inline"
                      placeholder="user@company.com"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleInvite()}
                    />
                    <select className="sa-select-inline" value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                      <option value="owner">Owner</option>
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <button className="sa-btn-primary" onClick={handleInvite} disabled={sendingInvite}>
                      {sendingInvite ? 'Sending…' : '📧 Invite'}
                    </button>
                  </div>
                  {lastInviteUrl && (
                    <div className="sa-invite-url-box">
                      <label>Invite Link (share with user):</label>
                      <div className="sa-invite-url-row">
                        <code className="sa-invite-url">{lastInviteUrl}</code>
                        <button className="sa-btn-sm" onClick={() => { navigator.clipboard.writeText(lastInviteUrl); setSuccess('Link copied!'); setTimeout(() => setSuccess(''), 2000); }}>
                          📋 Copy
                        </button>
                      </div>
                      <span className="sa-form-hint">Expires in 7 days. User will register via this link and auto-join the org.</span>
                    </div>
                  )}
                  {pendingInvites.length > 0 && (
                    <div className="sa-pending-invites">
                      <div className="sa-form-section-title" style={{ marginTop: 12 }}>Pending Invites</div>
                      {pendingInvites.map(inv => (
                        <div key={inv.id} className="sa-invite-row">
                          <div className="sa-member-info">
                            <div className="sa-member-name">{inv.email}</div>
                            <div className="sa-sub-text">
                              {inv.role} · Expires {new Date(inv.expires_at).toLocaleDateString()}
                            </div>
                          </div>
                          <button className="sa-btn-sm sa-btn-sm--red" onClick={() => handleCancelInvite(inv.id)} title="Cancel invite">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {expiredInvites.length > 0 && (
                    <div className="sa-sub-text" style={{ marginTop: 8, fontStyle: 'italic' }}>
                      {expiredInvites.length} expired invite{expiredInvites.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}

              {/* Add existing user */}
              <div className="sa-add-user-row" style={{ marginTop: showCreateUser || showInvite ? 16 : 0 }}>
                <input
                  className="sa-input-inline"
                  placeholder="Add existing user by email…"
                  value={addEmail}
                  onChange={e => setAddEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddUser()}
                />
                <select className="sa-select-inline" value={addRole} onChange={e => setAddRole(e.target.value)}>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button className="sa-btn-primary" onClick={handleAddUser}>Add</button>
              </div>

              <div className="sa-member-list">
                {data.members.map(m => (
                  <div key={m.user_id} className={`sa-member-row ${!m.is_active ? 'sa-member-row--inactive' : ''}`}>
                    <div className="sa-member-info">
                      <div className="sa-member-name">{m.name || m.email}</div>
                      <div className="sa-sub-text">{m.email}</div>
                    </div>
                    <select
                      className="sa-select-inline"
                      value={m.role}
                      onChange={e => handleRoleChange(m.user_id, e.target.value)}
                    >
                      <option value="owner">Owner</option>
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <span className={`sa-active-dot ${m.is_active ? 'active' : 'inactive'}`} title={m.is_active ? 'Active' : 'Inactive'} />
                    {m.is_active && (
                      <button
                        className="sa-btn-sm sa-btn-sm--blue"
                        onClick={() => handleImpersonateUser(m)}
                        title="View as this user (read-only support session)"
                      >🔍</button>
                    )}
                    <button className="sa-btn-sm sa-btn-sm--red" onClick={() => handleRemoveUser(m.user_id)} title="Remove">✕</button>
                  </div>
                ))}
              </div>
            </section>

            {/* LinkedIn Seats */}
            <section className="sa-drawer-section">
              <h3>LinkedIn Seats</h3>
              <SALinkedInSeats orgId={orgId} members={data.members} />
            </section>

            {/* Integrations */}
            <section className="sa-drawer-section">
              <h3>Integrations</h3>
              {data.integrations.length === 0 ? (
                <p className="sa-sub-text">No integrations configured</p>
              ) : (
                <div className="sa-integration-chips">
                  {data.integrations.map((i, idx) => (
                    <span key={idx} className={`sa-chip ${i.is_active ? 'sa-chip--active' : 'sa-chip--inactive'}`}>
                      {i.provider} {i.is_active ? '✓' : '○'}
                    </span>
                  ))}
                </div>
              )}
            </section>

            {/* Module Provisioning */}
            <section className="sa-drawer-section">
              <h3>Module Provisioning</h3>
              <SAOrgModules orgId={orgId} />
            </section>

          </div>
        )}
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────
// LINKEDIN SEATS — view / reassign / unbind user_linkedin_seats
//
// Seats bind lazily via the Chrome extension (bindSeat on first
// connection sync). This panel covers the two manual-fix cases:
// wrong-account binding (unbind → user re-syncs from the right
// account) and handover (reassign, preserving the slug binding so
// the new owner doesn't hit SEAT_CONFLICT).
// ─────────────────────────────────────────────────────────────────

function SALinkedInSeats({ orgId, members }) {
  const [seats, setSeats]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [reassigning, setReassigning] = useState(null); // seat id with reassign picker open
  const [reassignTo, setReassignTo]   = useState('');
  const [busySeat, setBusySeat]       = useState(null); // seat id with request in flight

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiService.superAdmin.getOrgLinkedInSeats(orgId);
      setSeats(r.data.seats || []);
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to load LinkedIn seats');
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const handleReassign = async (seat) => {
    const userId = parseInt(reassignTo, 10);
    if (!Number.isInteger(userId)) return;
    try {
      setBusySeat(seat.id); setError('');
      await apiService.superAdmin.reassignLinkedInSeat(orgId, seat.id, userId);
      setReassigning(null); setReassignTo('');
      flash(`Seat "${seat.public_identifier}" reassigned`);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to reassign seat');
    } finally { setBusySeat(null); }
  };

  const handleUnbind = async (seat) => {
    if (!window.confirm(
      `Unbind LinkedIn seat "${seat.public_identifier}" from ${seat.user_name || seat.user_email || 'this user'}?\n\n` +
      `This only removes the binding — if the same LinkedIn account runs a sync again, ` +
      `the seat re-creates and binds to whoever ran that sync.`
    )) return;
    try {
      setBusySeat(seat.id); setError('');
      await apiService.superAdmin.deleteLinkedInSeat(orgId, seat.id);
      flash(`Seat "${seat.public_identifier}" unbound`);
      load();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.error?.code === 'SEAT_HAS_ACTIVE_LEASES') {
        const n = e.response.data.error.activeLeases;
        if (window.confirm(
          `This seat has ${n} in-flight LinkedIn auto-send lease(s).\n\n` +
          `Unbinding now will not stop them, but the seat context will be gone. Unbind anyway?`
        )) {
          try {
            await apiService.superAdmin.deleteLinkedInSeat(orgId, seat.id, true);
            flash(`Seat "${seat.public_identifier}" unbound (forced)`);
            load();
          } catch (e2) {
            setError(e2.response?.data?.error?.message || 'Failed to unbind seat');
          }
        }
      } else {
        setError(e.response?.data?.error?.message || 'Failed to unbind seat');
      }
    } finally { setBusySeat(null); }
  };

  const activeMembers = (members || []).filter(m => m.is_active);

  if (loading) return <div className="sa-loading">Loading seats…</div>;

  return (
    <div className="sa-linkedin-seats">
      {error   && <div className="sa-alert sa-alert--error">⚠️ {error}<button onClick={() => setError('')}>✕</button></div>}
      {success && <div className="sa-alert sa-alert--success">✅ {success}</div>}

      {seats.length === 0 ? (
        <p className="sa-sub-text">
          No LinkedIn seats bound yet. Seats are created automatically the first time a user
          runs a connection sync from the Chrome extension while logged into LinkedIn.
        </p>
      ) : (
        <div className="sa-member-list">
          {seats.map(s => (
            <div key={s.id} className={`sa-member-row ${s.user_is_active === false ? 'sa-member-row--inactive' : ''}`}>
              <div className="sa-member-info">
                <div className="sa-member-name">
                  <a href={`https://www.linkedin.com/in/${encodeURIComponent(s.public_identifier)}/`}
                     target="_blank" rel="noopener noreferrer">
                    /in/{s.public_identifier}
                  </a>
                  {s.display_name ? ` — ${s.display_name}` : ''}
                </div>
                <div className="sa-sub-text">
                  Bound to {s.user_name || s.user_email || `user #${s.user_id}`}
                  {s.user_is_active === false ? ' (inactive member)' : ''}
                  {' · '}last seen {new Date(s.last_seen_at).toLocaleDateString()}
                  {Number(s.active_leases) > 0 ? ` · 🔥 ${s.active_leases} in-flight lease(s)` : ''}
                </div>
              </div>

              {reassigning === s.id ? (
                <>
                  <select
                    className="sa-select-inline"
                    value={reassignTo}
                    onChange={e => setReassignTo(e.target.value)}
                  >
                    <option value="">Reassign to…</option>
                    {activeMembers
                      .filter(m => m.user_id !== s.user_id)
                      .map(m => (
                        <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
                      ))}
                  </select>
                  <button
                    className="sa-btn-sm sa-btn-sm--blue"
                    onClick={() => handleReassign(s)}
                    disabled={!reassignTo || busySeat === s.id}
                  >
                    {busySeat === s.id ? '…' : '✓'}
                  </button>
                  <button className="sa-btn-sm" onClick={() => { setReassigning(null); setReassignTo(''); }}>✕</button>
                </>
              ) : (
                <>
                  <button
                    className="sa-btn-sm"
                    onClick={() => { setReassigning(s.id); setReassignTo(''); }}
                    title="Reassign seat to another member"
                    disabled={busySeat === s.id}
                  >
                    ↔️ Reassign
                  </button>
                  <button
                    className="sa-btn-sm sa-btn-sm--red"
                    onClick={() => handleUnbind(s)}
                    title="Unbind seat"
                    disabled={busySeat === s.id}
                  >
                    ✕
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="sa-form-hint" style={{ marginTop: 8 }}>
        Seats bind lazily on first extension sync — one LinkedIn account per org can bind to only
        one user. Unbinding does not stop a logged-in extension from re-binding on its next sync.
      </p>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// CHANGE 1 — NEW COMPONENT: SAOrgModules
//
// Paste this as a new function anywhere after the SAOrgDetail function,
// before SACreateOrg.
// ═════════════════════════════════════════════════════════════════════════════

const MODULE_DEFS_SA = [
  {
    key:   'prospecting',
    icon:  '🎯',
    label: 'Prospecting',
    desc:  'Prospect pipeline, outreach sequences, ICP scoring, sender accounts.',
  },
  {
    key:   'contracts',
    icon:  '📄',
    label: 'Contract Lifecycle Management',
    desc:  'CLM workflow — create contracts, legal review, e-signature, versioning.',
  },
  {
    key:   'handovers',
    icon:  '🤝',
    label: 'Sales → Implementation Project',
    desc:  'Structured project workflow on deal close — checklist, stakeholders, commitments.',
  },
  {
    key:   'service',
    icon:  '🎧',
    label: 'Customer Support & Service',
    desc:  'Case management, SLA tiers, playbook-driven workflows, team assignment.',
  },
  {
    key:   'agency',
    icon:  '🏢',
    label: 'Agency Client Management',
    desc:  'Manage client accounts — portals, team scoping, outreach tracking.',
  },
  {
    key:   'dailywork',
    icon:  '📋',
    label: 'Daily Work',
    desc:  'Daily activity logging against work items, with manager rollups by day, person, project or account.',
  },
];

/**
 * SAOrgModules — module provisioning panel inside the org detail drawer.
 *
 * Shows every module in MODULE_DEFS_SA. For each:
 *   - Allowed toggle: controlled by super admin (platform provisioning)
 *   - Enabled indicator: read-only view of what the org admin has turned on
 *
 * Disallowing a module automatically forces its enabled state to false
 * (enforced on the backend; reflected here immediately in the UI).
 */
function SAOrgModules({ orgId }) {
  const [modules, setModules]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(null); // key of module being saved
  const [seeding, setSeeding]   = useState(false);
  const [seedPlan, setSeedPlan] = useState(null);   // preview, before committing
  const [seedDone, setSeedDone] = useState(null);   // result, shown AT the button
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await apiService.superAdmin.getOrgModules(orgId);
      setModules(r.data.modules);
    } catch (e) {
      setError('Failed to load module settings');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const handleToggleAllowed = async (moduleKey, newAllowed) => {
    setSaving(moduleKey);
    setError('');
    try {
      const r = await apiService.superAdmin.updateOrgModules(orgId, { [moduleKey]: newAllowed });
      setModules(r.data.modules);
      const label = MODULE_DEFS_SA.find(m => m.key === moduleKey)?.label || moduleKey;
      setSuccess(`${label} ${newAllowed ? 'provisioned ✓' : 'removed from org'}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to update module');
    } finally {
      setSaving(null);
    }
  };

  /**
   * Seed the bare minimum Daily Work needs.
   *
   * A separate action rather than something Allowed does for you: provisioning
   * an org ahead of a sale should not silently write rows into it. Idempotent
   * and additive, so pressing it twice is safe and pressing it on an org that
   * was set up by hand changes nothing.
   */
  const handlePreviewDailyWork = async () => {
    setSeeding(true);
    setError('');
    setSeedDone(null);
    try {
      const r = await apiService.superAdmin.previewOrgDailyWork(orgId);
      setSeedPlan(r.data);
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not read the org\'s Daily Work setup');
    } finally {
      setSeeding(false);
    }
  };

  const handleSeedDailyWork = async () => {
    setSeeding(true);
    setError('');
    try {
      const r = await apiService.superAdmin.seedOrgDailyWork(orgId);
      const created = r.data?.created || [];
      // Reported AT THE BUTTON. The success alert renders above the module
      // list, and Daily Work is the last of six rows — so on any normal window
      // the confirmation appeared off-screen and the button looked inert.
      setSeedDone(created.length
        ? `Created ${created.join(' and ')}.`
        : 'Nothing was missing — no changes made.');
      setSeedPlan(null);
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to seed Daily Work');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) return <div className="sa-loading" style={{ padding: '12px 0' }}>Loading modules…</div>;

  return (
    <div>
      {error   && <div className="sa-alert sa-alert--error" style={{ marginBottom: 12 }}>⚠️ {error}</div>}
      {success && <div className="sa-alert sa-alert--success" style={{ marginBottom: 12 }}>✅ {success}</div>}

      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, lineHeight: 1.5 }}>
        <strong>Allowed</strong> controls whether the org can activate a module.
        Org admins can only turn on modules that are provisioned here.
        Removing access immediately disables the module for the org.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {MODULE_DEFS_SA.map(def => {
          const state      = modules?.[def.key] || { allowed: false, enabled: false };
          const isSaving   = saving === def.key;
          const isAllowed  = state.allowed;
          const isEnabled  = state.enabled;

          return (
            <div
              key={def.key}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '12px 14px',
                borderRadius: 10,
                border: `1px solid ${isAllowed ? '#d1fae5' : '#e5e7eb'}`,
                background: isAllowed ? '#f0fdf4' : '#fafafa',
                transition: 'all 0.15s',
              }}
            >
              {/* Icon + label */}
              <div style={{ fontSize: 20, marginTop: 1 }}>{def.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 2 }}>
                  {def.label}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}>{def.desc}</div>
                {/* Enabled indicator — read-only, set by org admin */}
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div
                    style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: isEnabled ? '#059669' : '#d1d5db',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 11, color: isEnabled ? '#059669' : '#9ca3af' }}>
                    {isEnabled ? 'Active — org has turned this on' : 'Inactive — org has not enabled this'}
                  </span>
                </div>

                {/* Daily Work starts completely empty — no activity list, no
                    holiday calendar — so an org admin who enables it lands on
                    four blank screens. This gives them one of each to work
                    from. Only offered once the module is provisioned: seeding
                    an org that may never get the module is writing rows for
                    nothing. */}
                {def.key === 'dailywork' && isAllowed && (
                  <div style={{ marginTop: 8 }}>
                    {/* Two steps, because "did that do anything?" is not a
                        question a provisioning button should leave you with.
                        Check first, then commit. */}
                    {!seedPlan && (
                      <button
                        disabled={seeding}
                        onClick={handlePreviewDailyWork}
                        title="Read-only. Shows what exists and what seeding would add."
                        style={{
                          fontSize: 11, fontWeight: 600, padding: '4px 10px',
                          borderRadius: 6, border: '1px solid #bae6fd',
                          background: '#f0f9ff', color: '#0369a1',
                          cursor: seeding ? 'default' : 'pointer',
                          opacity: seeding ? 0.6 : 1,
                        }}>
                        {seeding ? 'Checking…' : 'Check starter data'}
                      </button>
                    )}

                    {seedPlan && (
                      <div style={{
                        border: '1px solid #bae6fd', background: '#f8fbff',
                        borderRadius: 8, padding: '8px 10px', fontSize: 11,
                        color: '#374151', lineHeight: 1.5,
                      }}>
                        <div style={{ color: '#6b7280' }}>
                          This org has {seedPlan.current.activityTypes} activity{' '}
                          {seedPlan.current.activityTypes === 1 ? 'type' : 'types'}
                          {' '}({seedPlan.current.activeActivityTypes} active) and{' '}
                          {seedPlan.current.calendars} holiday{' '}
                          {seedPlan.current.calendars === 1 ? 'calendar' : 'calendars'}.
                        </div>

                        {seedPlan.wouldCreate.length > 0 ? (
                          <div style={{ marginTop: 6 }}>
                            <b>Would create:</b>
                            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                              {seedPlan.wouldCreate.map(x => <li key={x}>{x}</li>)}
                            </ul>
                          </div>
                        ) : (
                          <div style={{ marginTop: 6, color: '#059669' }}>
                            Nothing to create — the starter data is already there.
                          </div>
                        )}

                        {seedPlan.alreadyThere.length > 0 && (
                          <div style={{ marginTop: 6, color: '#9ca3af' }}>
                            Leaving alone: {seedPlan.alreadyThere.join(', ')}.
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <button
                            disabled={seeding || seedPlan.wouldCreate.length === 0}
                            onClick={handleSeedDailyWork}
                            style={{
                              fontSize: 11, fontWeight: 600, padding: '4px 10px',
                              borderRadius: 6, border: 'none',
                              background: seedPlan.wouldCreate.length ? '#0369a1' : '#d1d5db',
                              color: '#fff',
                              cursor: seedPlan.wouldCreate.length && !seeding ? 'pointer' : 'default',
                            }}>
                            {seeding ? 'Seeding…' : 'Seed it'}
                          </button>
                          <button onClick={() => setSeedPlan(null)}
                            style={{
                              fontSize: 11, padding: '4px 10px', borderRadius: 6,
                              border: '1px solid #e5e7eb', background: '#fff',
                              color: '#374151', cursor: 'pointer',
                            }}>
                            Close
                          </button>
                        </div>
                      </div>
                    )}

                    {seedDone && (
                      <div style={{ fontSize: 11, color: '#059669', marginTop: 6 }}>
                        ✅ {seedDone}
                      </div>
                    )}

                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4, lineHeight: 1.4 }}>
                      One placeholder activity type and a default holiday calendar.
                      The org admin then sets their own activity list, holidays,
                      working weeks and timezones.
                    </div>
                  </div>
                )}
              </div>

              {/* Allowed toggle */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <button
                  disabled={isSaving}
                  onClick={() => handleToggleAllowed(def.key, !isAllowed)}
                  title={isAllowed ? 'Remove access' : 'Provision access'}
                  style={{
                    position: 'relative', width: 42, height: 24, borderRadius: 12,
                    border: 'none',
                    background: isAllowed ? '#059669' : '#d1d5db',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    opacity: isSaving ? 0.6 : 1,
                    transition: 'background 0.2s',
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 4,
                    left: isAllowed ? 21 : 4,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#fff',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </button>
                <span style={{ fontSize: 10, fontWeight: 600, color: isAllowed ? '#059669' : '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  {isSaving ? '…' : (isAllowed ? 'Allowed' : 'Locked')}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// CREATE ORG MODAL
// ─────────────────────────────────────────────────────────────────

function SACreateOrg({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', plan: 'free', max_users: 10, notes: '', type: 'standard' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleCreate = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    try {
      setSaving(true);
      await apiService.superAdmin.createOrg(form);
      onCreated();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to create org');
    } finally { setSaving(false); }
  };

  return (
    <div className="sa-modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sa-modal">
        <div className="sa-modal-header">
          <h2>New Organisation</h2>
          <button className="sa-drawer-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="sa-alert sa-alert--error">⚠️ {error}</div>}
        <div className="sa-form-grid">
          <div className="sa-form-field sa-form-field--full">
            <label>Organisation Name *</label>
            <input
              autoFocus
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Acme Corp"
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
            />
          </div>
          <div className="sa-form-field">
            <label>Plan</label>
            <select value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })}>
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div className="sa-form-field">
            <label>Max Users</label>
            <input type="number" min="1" value={form.max_users} onChange={e => setForm({ ...form, max_users: parseInt(e.target.value) })} />
          </div>
          <div className="sa-form-field sa-form-field--full">
            <label>Organisation Type</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              <option value="standard">Standard — full customer org (seeded with default stages & playbooks)</option>
              <option value="assessment">Assessment — read-only toward the CRM, NOT seeded</option>
            </select>
            {form.type === 'assessment' && (
              <div className="sa-sub-text" style={{ marginTop: 4 }}>
                Assessment orgs: CRM write-back is hard-blocked (403), no default stages or
                playbooks are created, and the org can be converted to Standard later —
                conversion seeds it at that point.
              </div>
            )}
          </div>
          <div className="sa-form-field sa-form-field--full">
            <label>Notes (internal)</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="e.g. Enterprise pilot — Q3 deal" />
          </div>
        </div>
        <div className="sa-modal-footer">
          <button className="sa-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="sa-btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating…' : 'Create Organisation'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SUPER ADMINS TAB — manage who has platform access
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// USERS — platform-wide list across all orgs, with impersonation
// ─────────────────────────────────────────────────────────────────

function SAUsers({ initialNew30d = false }) {
  const [users, setUsers]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [new30d, setNew30d]   = useState(initialNew30d);
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage]       = useState(1);
  const [error, setError]     = useState('');

  const LIMIT = 20;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await apiService.superAdmin.getUsers({
        search,
        new_30d: new30d ? 'true' : undefined,
        active:  activeOnly ? 'true' : undefined,
        page,
        limit: LIMIT,
      });
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch (e) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, new30d, activeOnly, page]);

  useEffect(() => { load(); }, [load]);

  const handleImpersonate = async (u) => {
    const who = u.name?.trim() || u.email;
    if (!window.confirm(
      `Enter READ-ONLY support mode as "${who}"?\n\n` +
      `You'll see the app exactly as they do. Writes are disabled and this is logged.`
    )) return;
    try {
      setError('');
      const r = await apiService.superAdmin.impersonateUser(u.user_id);
      // Stash the real super-admin session, then swap in the impersonated one.
      localStorage.setItem('sa_token', localStorage.getItem('token'));
      localStorage.setItem('sa_user',  localStorage.getItem('user'));
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user',  JSON.stringify(r.data.user));
      // Full reload so the whole app re-initialises as the impersonated user.
      window.location.reload();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to start impersonation');
    }
  };

  const totalPages = Math.max(Math.ceil(total / LIMIT), 1);

  return (
    <div className="sa-panel">
      {error && <div className="sa-alert sa-alert--error">⚠️ {error}<button onClick={() => setError('')}>✕</button></div>}

      {/* Toolbar */}
      <div className="sa-toolbar">
        <input
          className="sa-search"
          placeholder="Search users by name, email, or organisation…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <label className="sa-check">
          <input
            type="checkbox"
            checked={new30d}
            onChange={e => { setNew30d(e.target.checked); setPage(1); }}
          /> New (30d)
        </label>
        <label className="sa-check">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={e => { setActiveOnly(e.target.checked); setPage(1); }}
          /> Active only
        </label>
      </div>

      {loading ? (
        <div className="sa-loading">Loading users…</div>
      ) : (
        <>
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Organisations</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={5} className="sa-empty">No users match.</td></tr>
                )}
                {users.map(u => (
                  <tr key={u.user_id}>
                    <td>
                      <div className="sa-user-name">
                        {u.name?.trim() || '—'}
                        {u.is_super_admin && <span className="sa-chip sa-chip--admin">Super Admin</span>}
                      </div>
                      <div className="sa-user-email">{u.email}</div>
                    </td>
                    <td>
                      {(u.orgs || []).length === 0
                        ? <span className="sa-muted">No org</span>
                        : (u.orgs || []).map(o => (
                            <div key={`${u.user_id}-${o.org_id}`} className="sa-user-org">
                              {o.org_name} <span className="sa-muted">· {o.role}</span>
                            </div>
                          ))}
                    </td>
                    <td>{u.joined_at ? new Date(u.joined_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <span className={`sa-chip ${u.is_active ? 'sa-chip--active' : 'sa-chip--inactive'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="sa-btn-secondary"
                        disabled={u.is_super_admin || (u.orgs || []).length === 0}
                        title={
                          u.is_super_admin ? 'Cannot impersonate a super admin'
                          : (u.orgs || []).length === 0 ? 'User has no organisation context'
                          : 'Impersonate (read-only)'
                        }
                        onClick={() => handleImpersonate(u)}
                      >
                        🔧 Impersonate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="sa-pagination">
            <button className="sa-btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span>Page {page} of {totalPages} · {total} users</span>
            <button className="sa-btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
}

function SAAdmins() {
  const [admins, setAdmins]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail]     = useState('');
  const [notes, setNotes]     = useState('');
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const load = async () => {
    try {
      setLoading(true);
      const r = await apiService.superAdmin.getAdmins();
      setAdmins(r.data.admins);
    } catch { setError('Failed to load admins'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleGrant = async () => {
    if (!email.trim()) return;
    try {
      await apiService.superAdmin.grantAdmin({ email: email.trim(), notes });
      setEmail(''); setNotes('');
      setSuccess(`Super admin granted to ${email}`);
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to grant access');
    }
  };

  const handleRevoke = async (userId, userEmail) => {
    if (!window.confirm(`Revoke super admin access from ${userEmail}?`)) return;
    try {
      await apiService.superAdmin.revokeAdmin(userId);
      setSuccess(`Access revoked`);
      setTimeout(() => setSuccess(''), 2000);
      load();
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to revoke');
    }
  };

  return (
    <div className="sa-panel">
      {error   && <div className="sa-alert sa-alert--error">⚠️ {error}<button onClick={() => setError('')}>✕</button></div>}
      {success && <div className="sa-alert sa-alert--success">✅ {success}</div>}

      <div className="sa-warning-box">
        <strong>⚠️ Restricted</strong> — Super admins have unrestricted access to all organisations, all data, and all user accounts across the platform. Grant sparingly.
      </div>

      {/* Grant form */}
      <div className="sa-card">
        <h3>Grant Super Admin Access</h3>
        <div className="sa-inline-form">
          <input
            className="sa-input"
            placeholder="user@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <input
            className="sa-input"
            placeholder="Notes (e.g. 'Founding engineer')"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleGrant()}
          />
          <button className="sa-btn-primary" onClick={handleGrant}>Grant Access</button>
        </div>
      </div>

      {/* Current admins */}
      <div className="sa-card">
        <h3>Current Super Admins</h3>
        {loading ? (
          <div className="sa-loading">Loading…</div>
        ) : (
          <div className="sa-member-list">
            {admins.map(a => (
              <div key={a.id} className={`sa-member-row ${a.revoked_at ? 'sa-member-row--inactive' : ''}`}>
                <div className="sa-member-info">
                  <div className="sa-member-name">
                    {a.name || a.email}
                    {a.user_id === currentUser.id && <span className="sa-you-badge">you</span>}
                  </div>
                  <div className="sa-sub-text">
                    {a.email} · Granted by {a.granted_by_email || 'system'} on {new Date(a.granted_at).toLocaleDateString()}
                    {a.notes && ` · ${a.notes}`}
                  </div>
                </div>
                {a.revoked_at ? (
                  <span className="sa-chip sa-chip--inactive">Revoked {new Date(a.revoked_at).toLocaleDateString()}</span>
                ) : (
                  <button
                    className="sa-btn-sm sa-btn-sm--red"
                    onClick={() => handleRevoke(a.user_id, a.email)}
                    disabled={a.user_id === currentUser.id}
                    title={a.user_id === currentUser.id ? 'Cannot revoke yourself' : 'Revoke access'}
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// AUDIT LOG
// ─────────────────────────────────────────────────────────────────

function SAAuditLog() {
  const [logs, setLogs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]   = useState(1);

  const ACTION_ICONS = {
    create_org: '🏢', update_org: '✏️', suspend_org: '⏸', unsuspend_org: '▶',
    impersonate_org: '🔧', add_user_to_org: '➕', remove_user_from_org: '➖',
    update_user_in_org: '👤', grant_super_admin: '🔐', revoke_super_admin: '🔒',
  };

  useEffect(() => {
    setLoading(true);
    apiService.superAdmin.getAuditLog({ page, limit: 50 })
      .then(r => setLogs(r.data.logs))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div className="sa-panel">
      <div className="sa-panel-info">
        Platform audit trail — immutable record of all super admin actions.
      </div>

      {loading ? (
        <div className="sa-loading">Loading…</div>
      ) : (
        <>
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={5} className="sa-empty">No audit entries yet</td></tr>
                )}
                {logs.map(log => (
                  <tr key={log.id}>
                    <td className="sa-sub-text">{new Date(log.created_at).toLocaleString()}</td>
                    <td>{log.admin_name || log.admin_email}</td>
                    <td>
                      <span className="sa-audit-action">
                        {ACTION_ICONS[log.action] || '•'} {log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="sa-sub-text">{log.target_type} #{log.target_id}</td>
                    <td className="sa-sub-text">{log.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="sa-pagination">
            <button className="sa-btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span className="sa-sub-text">Page {page}</span>
            <button className="sa-btn-sm" disabled={logs.length < 50} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// WORKFLOWS TAB — platform-scoped workflow + rule management
// Fifth flat tab, after Audit Log.
// Renders two sub-tabs:
//   Workflows   — WorkflowCanvas (super scope)
//   Exec Log    — ExecutionLog across all orgs (super scope)
// ─────────────────────────────────────────────────────────────────

function SAWorkflows() {
  const [subTab, setSubTab] = useState('canvas');

  const SUB_TABS = [
    { id: 'canvas', label: '⚙️ Workflows & Rules' },
    { id: 'log',    label: '📋 Execution Log'     },
  ];

  return (
    <div className="sa-panel">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#111827' }}>
          ⚙️ Platform Workflows
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
          Platform-scoped workflows and standalone rules are inherited by all organisations.
          Mark rules as <strong>is_locked = true</strong> to prevent org admins from modifying them.
        </p>
      </div>

      {/* Sub-tab bar */}
      <div className="gw-scroll-x" style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            style={{
              padding: '7px 16px',
              borderRadius: '7px 7px 0 0',
              border: '1px solid transparent',
              borderBottom: 'none',
              background: subTab === t.id ? '#fff' : 'transparent',
              borderColor: subTab === t.id ? '#e5e7eb' : 'transparent',
              borderBottomColor: subTab === t.id ? '#fff' : 'transparent',
              fontSize: 13,
              fontWeight: subTab === t.id ? 600 : 500,
              color: subTab === t.id ? '#111827' : '#6b7280',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'canvas' && <WorkflowCanvas scope="super" />}
      {subTab === 'log'    && <ExecutionLog   scope="super" />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PLATFORM SETTINGS TAB
// Manages platform-wide configuration stored in the platform_settings table.
// Super admins edit here; org admins see read-only platform defaults in their
// own OrgAdmin → Data Quality → Email Settings page.
// ─────────────────────────────────────────────────────────────────────────────

// ── Tag list editor (reused from OAEmailSettings pattern) ─────────────────────

function SATagListEditor({ items, onAdd, onRemove, placeholder, disabled }) {
  const [input, setInput] = React.useState('');

  const handleAdd = () => {
    const val = input.trim().toLowerCase();
    if (!val || items.includes(val)) { setInput(''); return; }
    onAdd(val);
    setInput('');
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, minHeight: 32 }}>
        {items.length === 0 && (
          <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', lineHeight: '28px' }}>
            No entries — org admins must configure their own
          </span>
        )}
        {items.map(item => (
          <span key={item} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 12, padding: '3px 8px', borderRadius: 5,
            background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#374151',
          }}>
            {item}
            {!disabled && (
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, lineHeight: 1, padding: 0 }}
                onClick={() => onRemove(item)}
              >×</button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            style={{ flex: 1, padding: '6px 10px', borderRadius: 7, border: '1px solid #d1d5db', fontSize: 12 }}
            placeholder={placeholder}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <button
            style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#374151' }}
            onClick={handleAdd}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main SAPlatformSettings component ─────────────────────────────────────────

function SAPlatformSettings() {
  const [subTab, setSubTab]         = React.useState('email-filter');
  return (
    <div className="sa-panel">
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#111827' }}>
          🛠️ Platform Settings
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
          Platform-wide configuration that cascades to all organisations.
          Org admins can add their own settings on top but cannot remove platform defaults.
        </p>
      </div>

      {/* Sub-tab bar */}
      <div className="gw-scroll-x" style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 24 }}>
        {[
          { id: 'email-filter', label: '📧 Email Filter Defaults' },
          { id: 'integrations', label: '🔌 CRM Integrations' },
          { id: 'ai',           label: '🧠 AI Providers' },
          { id: 'wa-sessions',  label: '📥 WhatsApp Capture' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            style={{
              padding: '7px 16px',
              borderRadius: '7px 7px 0 0',
              border: '1px solid transparent',
              borderBottom: 'none',
              background: subTab === t.id ? '#fff' : 'transparent',
              borderColor: subTab === t.id ? '#e5e7eb' : 'transparent',
              borderBottomColor: subTab === t.id ? '#fff' : 'transparent',
              fontSize: 13,
              fontWeight: subTab === t.id ? 600 : 500,
              color: subTab === t.id ? '#111827' : '#6b7280',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'email-filter' && <SAEmailFilterSettings />}
      {subTab === 'integrations' && <SAPlatformIntegrations />}
      {subTab === 'ai'           && <SAAIPlatformSettings />}
      {subTab === 'wa-sessions'  && <SAWhatsAppSessions />}
    </div>
  );
}

// ── SAPlatformIntegrations ─────────────────────────────────────────────────────
// Controls the global feature gates for CRM integrations.
// SuperAdmin enables write-back capability here; Org Admins configure per-org.

function SAPlatformIntegrations() {
  const [writeBackEnabled, setWriteBackEnabled] = React.useState(false);
  const [loading,  setLoading]  = React.useState(true);
  const [saving,   setSaving]   = React.useState(false);
  const [dirty,    setDirty]    = React.useState(false);
  const [error,    setError]    = React.useState('');
  const [success,  setSuccess]  = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    apiService.superAdmin.getPlatformSetting('sf_write_back_enabled')
      .then(r => {
        setWriteBackEnabled(r.data?.value?.enabled || false);
        setDirty(false);
      })
      .catch(() => setWriteBackEnabled(false))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await apiService.superAdmin.updatePlatformSetting('sf_write_back_enabled', { enabled: writeBackEnabled });
      setSuccess('Integration settings saved ✓');
      setTimeout(() => setSuccess(''), 4000);
      setDirty(false);
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="sa-loading">Loading integration settings…</div>;

  return (
    <div>
      <div className="sa-warning-box" style={{ marginBottom: 20 }}>
        <strong>⚠️ Platform-wide effect</strong> — These settings control what Org Admins are
        allowed to configure. Enabling write-back here does not turn it on for any org —
        each Org Admin must enable it separately in their Salesforce settings.
      </div>

      {error   && <div className="sa-alert sa-alert--error">⚠️ {error}<button onClick={() => setError('')}>✕</button></div>}
      {success && <div className="sa-alert sa-alert--success">✅ {success}</div>}

      {/* Salesforce section */}
      <div className="sa-card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#111827' }}>
          ☁️ Salesforce
        </h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
          Controls what Salesforce features Org Admins can configure.
        </p>

        {/* Write-back toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
              Enable Salesforce write-back capability
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, lineHeight: 1.5 }}>
              When on, Org Admins can configure GoWarm to push completed actions back to
              Salesforce as Tasks. Off by default — each org still needs to enable it separately.
            </div>
          </div>
          <div
            style={{
              width: 44, height: 24, borderRadius: 12, flexShrink: 0, marginLeft: 20,
              background: writeBackEnabled ? '#1A3A5C' : '#d1d5db',
              position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
            }}
            onClick={() => { setWriteBackEnabled(p => !p); setDirty(true); }}
          >
            <div style={{
              position: 'absolute', top: 3,
              left: writeBackEnabled ? 23 : 3,
              width: 18, height: 18, borderRadius: '50%', background: '#fff',
              transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </div>
        </div>

        <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 7, fontSize: 12, color: '#0369a1', lineHeight: 1.5 }}>
          <strong>How write-back works:</strong> Completed GoWarm actions on deals/contacts that
          have a Salesforce ID are pushed as SF Tasks nightly (04:30 UTC) or in real-time
          depending on the org's configuration. A <code style={{ background: '#e0f2fe', padding: '1px 4px', borderRadius: 3 }}>GoWarm_Source__c</code> custom field
          on SF Tasks prevents echo loops — GoWarm won't re-import tasks it wrote.
        </div>
      </div>

      {/* Future CRMs placeholder */}
      <div className="sa-card" style={{ marginBottom: 20, opacity: 0.6 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#6b7280' }}>
          🔜 HubSpot / Zoho / Pipedrive
        </h3>
        <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>
          Coming in future phases. These integrations will use the same <code>external_refs</code> schema
          as Salesforce — no DB changes required when they launch.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          className="sa-btn-primary"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving…' : 'Save integration settings'}
        </button>
        <button
          className="sa-btn-secondary"
          onClick={() => {
            apiService.superAdmin.getPlatformSetting('sf_write_back_enabled')
              .then(r => { setWriteBackEnabled(r.data?.value?.enabled || false); setDirty(false); })
              .catch(() => {});
          }}
          disabled={saving || !dirty}
        >
          Discard changes
        </button>
      </div>
    </div>
  );
}

// ── SAEmailFilterSettings ─────────────────────────────────────────────────────

function SAEmailFilterSettings() {
  const [domains,   setDomains]   = React.useState([]);
  const [patterns,  setPatterns]  = React.useState([]);
  const [loading,   setLoading]   = React.useState(true);
  const [saving,    setSaving]    = React.useState(false);
  const [dirty,     setDirty]     = React.useState(false);
  const [error,     setError]     = React.useState('');
  const [success,   setSuccess]   = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    apiService.superAdmin.getPlatformSetting('email_filter')
      .then(r => {
        const val = r.data.value || {};
        setDomains(val.blocked_domains        || []);
        setPatterns(val.blocked_local_patterns || []);
        setDirty(false);
      })
      .catch(e => setError(e.response?.data?.error?.message || 'Failed to load platform email filter'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await apiService.superAdmin.updatePlatformSetting('email_filter', {
        blocked_domains:        domains,
        blocked_local_patterns: patterns,
      });
      setSuccess('Platform email filter saved ✓ — will apply on next email sync for all orgs');
      setTimeout(() => setSuccess(''), 5000);
      setDirty(false);
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const addDomain  = d => { setDomains(prev => [...prev, d]);   setDirty(true); };
  const rmDomain   = d => { setDomains(prev => prev.filter(x => x !== d)); setDirty(true); };
  const addPattern = p => { setPatterns(prev => [...prev, p]);  setDirty(true); };
  const rmPattern  = p => { setPatterns(prev => prev.filter(x => x !== p)); setDirty(true); };

  const clearAll = () => {
    if (!window.confirm('Clear all platform defaults? Org admins will need to configure their own filters from scratch.')) return;
    setDomains([]);
    setPatterns([]);
    setDirty(true);
  };

  if (loading) return <div className="sa-loading">Loading platform email filter…</div>;

  return (
    <div>
      {/* Warning banner */}
      <div className="sa-warning-box" style={{ marginBottom: 20 }}>
        <strong>⚠️ Platform-wide effect</strong> — Changes here apply to all organisations on the next email sync.
        Leaving both lists empty means org admins must configure their own filters from scratch.
        Org-specific additions are always merged on top of these defaults.
      </div>

      {error   && <div className="sa-alert sa-alert--error">⚠️ {error}<button onClick={() => setError('')}>✕</button></div>}
      {success && <div className="sa-alert sa-alert--success">✅ {success}</div>}

      {/* Blocked domains */}
      <div className="sa-card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 4 }}>Blocked domains</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Emails from these domains are dropped before matching. Applies to the sender address
          for received emails, and the recipient for sent emails.
        </p>
        <SATagListEditor
          items={domains}
          onAdd={addDomain}
          onRemove={rmDomain}
          placeholder="e.g. microsoft.com or mail.onedrive.com"
        />
      </div>

      {/* Blocked sender patterns */}
      <div className="sa-card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 4 }}>Blocked sender patterns</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Emails where the local part (before @) contains any of these patterns are dropped.
          E.g. adding <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 4, fontSize: 12 }}>noreply</code> blocks
          noreply@anycompany.com, no-reply@anycompany.com etc.
        </p>
        <SATagListEditor
          items={patterns}
          onAdd={addPattern}
          onRemove={rmPattern}
          placeholder="e.g. noreply or mailer-daemon"
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          className="sa-btn-primary"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving…' : 'Save platform defaults'}
        </button>
        <button
          className="sa-btn-secondary"
          onClick={() => {
            apiService.superAdmin.getPlatformSetting('email_filter').then(r => {
              const val = r.data.value || {};
              setDomains(val.blocked_domains        || []);
              setPatterns(val.blocked_local_patterns || []);
              setDirty(false);
            });
          }}
          disabled={saving || !dirty}
        >
          Discard changes
        </button>
        <button
          style={{ marginLeft: 'auto', padding: '7px 14px', borderRadius: 7, border: '1px solid #fca5a5', background: '#fff', fontSize: 13, color: '#dc2626', cursor: 'pointer' }}
          onClick={clearAll}
          disabled={saving}
        >
          Clear all defaults
        </button>
      </div>

      {/* How it stacks */}
      <div style={{ marginTop: 24, padding: '14px 16px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 9 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          How filtering stacks
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, fontSize: 12, color: '#374151' }}>
          {[
            { label: 'Platform defaults', sub: 'set here', color: '#6366f1', bg: '#eef2ff' },
            { label: '+', sub: '', color: '#9ca3af', bg: 'transparent' },
            { label: 'Org additions', sub: 'set by org admin', color: '#059669', bg: '#f0fdf4' },
            { label: '=', sub: '', color: '#9ca3af', bg: 'transparent' },
            { label: 'Effective filter', sub: 'applied at sync time', color: '#374151', bg: '#f3f4f6' },
          ].map((item, i) => (
            <div key={i} style={{
              padding: item.bg === 'transparent' ? '0 8px' : '8px 14px',
              borderRadius: 7,
              background: item.bg,
              textAlign: 'center',
            }}>
              {item.bg !== 'transparent' && (
                <>
                  <div style={{ fontWeight: 600, color: item.color, fontSize: 12 }}>{item.label}</div>
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{item.sub}</div>
                </>
              )}
              {item.bg === 'transparent' && (
                <span style={{ fontSize: 18, color: '#9ca3af', fontWeight: 300 }}>{item.label}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
